/*
 * The authenticated frame: the permission-filtered SideNav plus the page header, wrapping each
 * section's content. Nav items come from `visibleSections(claims.permissions)` so the rail only ever
 * shows what the operator's role allows (admin-ui.md matrix); the active section is marked from the
 * current path. Logout drops the token via the auth context and the guard bounces to /login. The
 * SideNav's role label uses the humanised role name.
 */
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { PageShell, SideNav, type SideNavItem } from '@cou/admin-ui';
import { useAdminAuth } from '../session/admin-auth-context';
import { visibleSections } from './nav-sections';
import { NAV_ICONS } from './nav-icons';

/** Humanise a role id (SUPER_ADMIN) into a nav label (Super Admin). */
function humaniseRole(role: string): string {
  return role
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export interface AdminShellProps {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  /** Row above the title (status pill, age chip, id chip) — contest detail pattern. */
  readonly meta?: ReactNode;
  /** Right-aligned header actions (e.g. a primary "+ New X" button), next to the title. */
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}

export function AdminShell({ title, subtitle, meta, actions, children }: AdminShellProps) {
  const { claims, signOut } = useAdminAuth();
  const { pathname } = useLocation();

  const permissions = claims?.permissions ?? [];
  const items: SideNavItem[] = visibleSections(permissions).map((section) => ({
    label: section.label,
    icon: NAV_ICONS[section.key],
    href: section.path,
    // Contest detail lives under /contests/:id — keep Contests marked current there too.
    current:
      section.path === pathname ||
      (section.key === 'contests' && pathname.startsWith('/contests/')),
  }));

  return (
    <PageShell
      title={title}
      subtitle={subtitle}
      meta={meta}
      actions={actions}
      nav={
        <SideNav
          name="Caesars Admin"
          role={claims ? humaniseRole(claims.role) : undefined}
          items={items}
          onLogout={signOut}
        />
      }
    >
      {children}
    </PageShell>
  );
}
